import React, { useState, useRef, DragEvent } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CardMedia,
  IconButton,
  CircularProgress,
  Chip,
  Paper,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { Task } from '../../../../redux/task/types'
import { useAppSelector, useAppDispatch } from '../../../../redux/store'
import { selectUserTeamMemberInfo } from '../../../../redux/auth/selector'
import showToast from '../../../Common/SweetAlert/ToastAlert'
import { useFetchImageUrl } from '../../../../hooks/componentHooks/useFetchImageUrl'
import axios from '../../../../config/axios'
import { fetchAssignedTaskDetails } from '../../../../redux/task/actions'
import { gradientColors, textColors } from '../../../Common/Color/Color'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'

interface TaskAttachImageModalProps {
  task: Task
  onImageAttached?: () => void
  open?: boolean
  onClose?: () => void
}

const TaskAttachImageModal: React.FC<TaskAttachImageModalProps> = ({
  task,
  onImageAttached,
  open: externalOpen,
  onClose: externalOnClose,
}) => {
  const [internalOpen, setInternalOpen] = useState(false)

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()

  // Fetch current team member task image if it exists
  const { imageUrl: currentImageUrl } = useFetchImageUrl(
    'images',
    task.imageFilename || null,
    'task',
    Boolean(task.imageFilename && task.imageFilename !== 'DefaultTaskImage'),
  )

  const handleOpen = () => {
    if (externalOpen === undefined) {
      setInternalOpen(true)
    }
    // Reset state when opening
    setSelectedFile(null)
    setImagePreview(null)
  }

  const handleClose = () => {
    if (selectedFile) {
      openConfirmationAlert({
        title: 'Discard changes',
        text: 'Are you sure you want to discard your changes?',
        onConfirm: () => {
          if (externalOnClose) {
            externalOnClose()
          } else {
            setInternalOpen(false)
          }
          setSelectedFile(null)
          setImagePreview(null)
          // Clean up preview URL
          if (imagePreview && selectedFile) {
            URL.revokeObjectURL(imagePreview)
          }
        },
      })
    } else {
      // Close the modal whether it's controlled externally or internally
      if (externalOnClose) {
        externalOnClose()
      } else {
        setInternalOpen(false)
      }
      // Clean up state
      setSelectedFile(null)
      setImagePreview(null)
    }
  }

  const validateAndSetFile = (file: File) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']
    if (!allowedTypes.includes(file.type)) {
      showToast('error', 'Only JPEG, JPG, and PNG images are allowed!')
      return
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024 // 5MB in bytes
    if (file.size > maxSize) {
      showToast('error', 'File size must be less than 5MB!')
      return
    }

    setSelectedFile(file)

    // Create preview URL
    const previewUrl = URL.createObjectURL(file)
    setImagePreview(previewUrl)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      validateAndSetFile(file)
    }
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (file) {
      validateAndSetFile(file)
    }
  }

  const handleAttachImage = async () => {
    if (!selectedFile) {
      showToast('error', 'Please select an image file')
      return
    }

    setUploading(true)
    try {
      // Create FormData for file upload
      const formData = new FormData()
      formData.append('imageFile', selectedFile)
      formData.append('id', task.id!.toString())
      formData.append('title', task.title)
      formData.append('details', task.details)
      formData.append('importance', task.importance)
      formData.append('dueDate', task.dueDate?.toString() || '')
      formData.append('link', task.link || '')
      formData.append('updatedBy[email]', teamMemberDetails.email)
      formData.append('updatedBy[workdayId]', teamMemberDetails.workdayId)

      // Add operational managers
      if (task.operationalManager) {
        task.operationalManager.forEach((manager, index) => {
          formData.append(`operationalManager[${index}][name]`, manager.name)
          formData.append(`operationalManager[${index}][workdayId]`, manager.workdayId)
        })
      }

      // Add functional areas
      if (task.functionalArea) {
        task.functionalArea.forEach((area, index) => {
          formData.append(`functionalArea[${index}]`, area)
        })
      }

      // Add supervisors
      if (task.supervisor) {
        task.supervisor.forEach((supervisor, index) => {
          formData.append(`supervisor[${index}][name]`, supervisor.name)
          formData.append(`supervisor[${index}][workdayId]`, supervisor.workdayId)
        })
      }

      // Make direct API call for file upload to the new endpoint
      await axios.patch(
        `/task/updateAssignedTaskWithImage?taskId=${task.id}&teamMemberWorkdayId=${teamMemberDetails.workdayId}&teamMemberEmail=${teamMemberDetails.email}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )

      // Refresh the task data
      await dispatch(
        fetchAssignedTaskDetails({
          teamMemberEmail: teamMemberDetails.email,
          teamMemberWorkdayId: teamMemberDetails.workdayId,
        }),
      )

      showToast('success', 'Image attached successfully!')
      handleClose()
      onImageAttached?.()
    } catch (error) {
      console.error('Error attaching image:', error)
      showToast('error', 'Failed to attach image. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleRemoveSelection = () => {
    if (imagePreview && selectedFile) {
      URL.revokeObjectURL(imagePreview)
    }
    setSelectedFile(null)
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const displayImage = imagePreview || currentImageUrl

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size='small'
        title='Attach Image'
        sx={{
          color: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'white',
          },
        }}
      >
        <AttachFileIcon fontSize='small' />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <Box sx={{ background: gradientColors.greenGradient, position: 'relative' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', padding: '16px 48px 16px 24px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DialogTitle
                sx={{
                  color: textColors.white,
                  fontWeight: 'bold',
                  fontSize: fontSizes.size20,
                  padding: 0,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-all',
                  whiteSpace: 'normal',
                  flex: 1,
                }}
              >
                {task.title}
              </DialogTitle>
              {task.required ? (
                <Chip
                  label='Documentation Required'
                  size='small'
                  sx={{
                    backgroundColor: '#e12339',
                    color: 'white',
                    fontWeight: 'bold',
                    '& .MuiChip-label': {
                      fontSize: '0.75rem',
                    },
                  }}
                />
              ) : (
                <Chip
                  label='Documentation Optional'
                  size='small'
                  sx={{
                    backgroundColor: '#3366cc',
                    color: 'white',
                    fontWeight: 'bold',
                    '& .MuiChip-label': {
                      fontSize: '0.75rem',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: textColors.white,
            }}
            size='small'
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          {/* Drag and Drop Upload Area with Integrated Preview */}
          <Box sx={{ width: '100%', my: 2 }}>
            <Paper
              sx={{
                p: displayImage ? 2 : 3,
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                minHeight: displayImage ? 'auto' : '200px',
                position: 'relative',
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type='file'
                accept='image/jpeg,image/jpg,image/png'
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {displayImage ? (
                // Image Preview Mode
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component='img'
                    image={displayImage}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: 300,
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                  {selectedFile && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveSelection()
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)',
                        },
                      }}
                      size='small'
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  )}
                </Box>
              ) : (
                // Upload Mode
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    minHeight: '160px',
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  <Typography variant='body2' color='textSecondary' textAlign='center'>
                    Drag and drop your image file here, or click to select
                  </Typography>
                  <Typography variant='caption' color='textSecondary' textAlign='center'>
                    Supported formats: JPEG, JPG, PNG (Max 5MB)
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleAttachImage}
            variant='contained'
            color='primary'
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : undefined}
          >
            {uploading ? 'Attaching...' : 'Attach'}
          </Button>
        </DialogActions>
        {ConfirmationAlert}
      </Dialog>
    </>
  )
}

export default TaskAttachImageModal
